<template>
  <v-dialog :model-value="modelValue" max-width="520px" persistent @update:model-value="$emit('update:modelValue', $event)">
    <v-card>
      <v-card-title class="bg-primary text-white d-flex align-center">
        <v-icon class="mr-2">{{ isAdd ? 'mdi-plus' : 'mdi-pencil-outline' }}</v-icon>
        <span>{{ isAdd ? $t('accessLevelLadderAddTitle') : $t('accessLevelLadderEditTitle', { code: form.code }) }}</span>
      </v-card-title>
      <v-card-text class="pt-6">
        <v-alert v-if="error" type="error" variant="tonal" density="comfortable" class="mb-4">{{ error }}</v-alert>
        <v-form ref="form" v-model="formValid" @submit.prevent="submit">
          <v-text-field
            :model-value="form.code"
            :label="$t('accessLevelLadderCode')"
            :hint="isAdd ? $t('accessLevelLadderCodeHint') : $t('accessLevelLadderCodeLockedHint')"
            persistent-hint
            :rules="isAdd ? [rules.codeRequired, rules.codeFormat, rules.codeUnique] : []"
            :disabled="!isAdd"
            :maxlength="codeMaxLength"
            prepend-inner-icon="mdi-identifier"
            variant="outlined"
            density="comfortable"
            @update:model-value="onCodeInput"
          />
          <v-select
            v-if="isAdd"
            v-model="form.insertAfterCode"
            :items="insertAfterOptions"
            item-title="title"
            item-value="value"
            :label="$t('accessLevelLadderInsertAfter')"
            :hint="$t('accessLevelLadderInsertAfterHint')"
            persistent-hint
            prepend-inner-icon="mdi-format-vertical-align-bottom"
            variant="outlined"
            density="comfortable"
            class="mt-4"
          />
          <v-text-field
            v-model="form.nameTh"
            :label="$t('accessLevelLadderNameTh')"
            :rules="[rules.nameMax]"
            :counter="nameMaxLength"
            variant="outlined"
            density="comfortable"
            class="mt-4"
          />
          <v-text-field
            v-model="form.nameEn"
            :label="$t('accessLevelLadderNameEn')"
            :rules="[rules.nameMax]"
            :counter="nameMaxLength"
            variant="outlined"
            density="comfortable"
            class="mt-2"
          />
          <v-textarea
            v-model="form.description"
            :label="$t('accessLevelLadderDescription')"
            :rules="[rules.descriptionMax]"
            :counter="descriptionMaxLength"
            rows="3"
            auto-grow
            variant="outlined"
            density="comfortable"
            class="mt-2"
          />
        </v-form>
      </v-card-text>
      <v-card-actions class="pb-4 px-6">
        <v-spacer />
        <v-btn variant="text" class="text-none" :disabled="saving" @click="$emit('update:modelValue', false)">
          {{ $t('accessLevelLadderCancel') }}
          <v-tooltip activator="parent" location="top">{{ $t('accessLevelLadderCancel') }}</v-tooltip>
        </v-btn>
        <v-btn color="primary" variant="flat" class="text-none" :loading="saving" :disabled="formValid === false" @click="submit">
          {{ $t('accessLevelLadderSave') }}
          <v-tooltip activator="parent" location="top">{{ $t('accessLevelLadderSave') }}</v-tooltip>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/** NameTh/NameEn NVARCHAR(200) · Description NVARCHAR(1000) · Code NVARCHAR(20) (ตรง `docs/sql/20261021/AccessLevel.sql`) */
const NAME_MAX_LENGTH = 200;
const DESCRIPTION_MAX_LENGTH = 1000;
const CODE_MAX_LENGTH = 20;
/** รูปแบบเดียวกับ `AccessLevelLadder.TryCreate` ฝั่ง server */
const LEVEL_CODE_PATTERN = /^T\d+(\.\d+)?$/;

function upperText(value) {
  return value == null ? '' : String(value).trim().toUpperCase();
}

function emptyForm() {
  return { code: '', nameTh: '', nameEn: '', description: '', insertAfterCode: '' };
}

/**
 * dialog เพิ่ม / แก้ชื่อระดับของหน้า `/AccessLevelConfig` (#914) — ตรวจฟอร์มเอง แล้ว emit `submit`
 * `{ code, nameTh, nameEn, description, insertAfterCode }` ให้หน้าแม่ยิง API (`POST /create` หรือ `PUT /{id}`)
 * · เพิ่ม: Code ตัวพิมพ์ใหญ่ ตรงรูปแบบ `T<เลข>[.<เลข>]` และไม่ซ้ำ (ไม่สนตัวพิมพ์) + "แทรกหลัง" (ไม่มี T3)
 * · แก้ชื่อ: Code ล็อก (แก้ไม่ได้หลังสร้าง)
 */
export default {
  name: 'AccessLevelTierDialog',
  props: {
    modelValue: { type: Boolean, default: false },
    /** 'add' | 'edit' */
    mode: { type: String, default: 'add' },
    /** ระดับที่จะแก้ (mode edit) `{ code, nameTh, nameEn, description }` */
    level: { type: Object, default: null },
    /** code ที่มีอยู่แล้ว (ตัวพิมพ์ใหญ่) — กัน Code ซ้ำ */
    existingCodes: { type: Array, default: () => [] },
    /** ตัวเลือก "แทรกหลัง" `[{ title, value }]` (ไม่มี T3) */
    insertAfterOptions: { type: Array, default: () => [] },
    /** ค่าเริ่มต้นของ "แทรกหลัง" (ระดับรองสุดท้าย = ใต้ T3) */
    defaultInsertAfter: { type: String, default: '' },
    saving: { type: Boolean, default: false },
    error: { type: String, default: '' },
  },
  emits: ['update:modelValue', 'submit'],
  data() {
    return {
      form: emptyForm(),
      formValid: null,
      nameMaxLength: NAME_MAX_LENGTH,
      descriptionMaxLength: DESCRIPTION_MAX_LENGTH,
      codeMaxLength: CODE_MAX_LENGTH,
    };
  },
  computed: {
    isAdd() {
      return this.mode === 'add';
    },
    rules() {
      return {
        codeRequired: (v) => !!upperText(v) || this.$t('accessLevelLadderCodeRequired'),
        codeFormat: (v) => {
          const code = upperText(v);
          return (code.length <= CODE_MAX_LENGTH && LEVEL_CODE_PATTERN.test(code)) || this.$t('accessLevelLadderCodeFormat');
        },
        codeUnique: (v) => !this.existingCodes.includes(upperText(v)) || this.$t('accessLevelLadderCodeDuplicate'),
        nameMax: (v) => !v || v.length <= NAME_MAX_LENGTH || this.$t('accessLevelLadderMaxLength', { max: NAME_MAX_LENGTH }),
        descriptionMax: (v) =>
          !v || v.length <= DESCRIPTION_MAX_LENGTH || this.$t('accessLevelLadderMaxLength', { max: DESCRIPTION_MAX_LENGTH }),
      };
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(open) {
        if (open) this.resetForm();
      },
    },
  },
  methods: {
    resetForm() {
      const level = this.level || {};
      this.form = this.isAdd
        ? { ...emptyForm(), insertAfterCode: this.defaultInsertAfter || '' }
        : {
            code: level.code || '',
            nameTh: level.nameTh || '',
            nameEn: level.nameEn || '',
            description: level.description || '',
            insertAfterCode: '',
          };
      this.formValid = null;
    },
    onCodeInput(value) {
      this.form.code = value == null ? '' : String(value).toUpperCase();
    },
    async submit() {
      if (this.saving) return;
      const form = this.$refs.form;
      if (form) {
        const validation = await form.validate();
        if (!validation.valid) return;
      }
      this.$emit('submit', {
        code: upperText(this.form.code),
        nameTh: String(this.form.nameTh || '').trim(),
        nameEn: String(this.form.nameEn || '').trim(),
        description: String(this.form.description || '').trim(),
        insertAfterCode: this.isAdd ? this.form.insertAfterCode || null : null,
      });
    },
  },
};
</script>
